import { useMemo } from "react"
import {
  useFetchIntegrationsQuery,
  useFetchBrandsQuery
} from "../../redux/queries"
import { Integration } from "../../types"

interface UseIntegrationsPageDataOpts {
  isSuperAdmin: boolean
  orgId?: string
  /** `""` = no API filter (all brands) */
  selectedBrandId?: string
}

function startOfTodayTimestamp(): number {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return Math.floor(d.getTime() / 1000)
}

function sortByCreatedDesc(items: Integration[]): Integration[] {
  return [...items].sort((a, b) => {
    const aTs = toCreatedTimestamp(a.created)
    const bTs = toCreatedTimestamp(b.created)
    return bTs - aTs
  })
}

function toCreatedTimestamp(created?: string): number {
  return parseInt(created ?? "0", 10)
}

export function useIntegrationsPageData({
  isSuperAdmin,
  orgId,
  selectedBrandId
}: UseIntegrationsPageDataOpts) {
  const todayStart = useMemo(() => startOfTodayTimestamp(), [])

  const listParams = useMemo(
    () => ({
      full_detail: true,
      limit: 999,
      ...(isSuperAdmin ? {} : { org_id: orgId }),
      ...(selectedBrandId ? { brand_id: selectedBrandId } : {})
    }),
    [isSuperAdmin, orgId, selectedBrandId]
  )

  const brandsQueryParams = useMemo(
    () => ({ limit: 999, ...(isSuperAdmin ? {} : { org_id: orgId }) }),
    [isSuperAdmin, orgId]
  )

  const { data: brands } = useFetchBrandsQuery(brandsQueryParams)

  const { data: listResponse, isLoading: isLoadingIntegrations } =
    useFetchIntegrationsQuery(listParams, { refetchOnMountOrArgChange: true })

  const allIntegrations = useMemo(() => {
    const sorted = sortByCreatedDesc(listResponse?.results ?? [])
    if (!selectedBrandId) return sorted
    return sorted.filter(
      integration => integration.brand_id === selectedBrandId
    )
  }, [listResponse, selectedBrandId])

  const todayIntegrations = useMemo(
    () =>
      allIntegrations.filter(
        integration => toCreatedTimestamp(integration.created) >= todayStart
      ),
    [allIntegrations, todayStart]
  )

  const historyIntegrations = useMemo(
    () =>
      allIntegrations.filter(
        integration => toCreatedTimestamp(integration.created) < todayStart
      ),
    [allIntegrations, todayStart]
  )

  return {
    brands,
    todayIntegrations,
    integrations: historyIntegrations,
    isLoadingIntegrations
  }
}
